/*
 * Size guide modal for the product page.
 *
 * Accessible dialog: role="dialog" + aria-modal on the markup, focus is moved
 * into the dialog on open, trapped while open, and returned to the trigger on
 * close. Closes on Escape, on the visible Close button and on overlay click.
 */
(function () {
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  class SizeGuideModal extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-size-guide-dialog]');
      this.overlay = this.querySelector('[data-size-guide-overlay]');

      if (this.overlay) this.overlay.addEventListener('click', this.handleOverlayClick);
      this.querySelectorAll('[data-size-guide-close]').forEach(
        function (button) {
          button.addEventListener('click', this.close);
        }.bind(this)
      );

      if (!this.hasAttribute('hidden')) this.setAttribute('hidden', '');
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove('size-guide-modal-open');
    }

    get isOpen() {
      return !this.hasAttribute('hidden');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.removeAttribute('hidden');
      document.body.classList.add('size-guide-modal-open');
      document.addEventListener('keydown', this.handleKeydown);

      var target = this.querySelector('[data-size-guide-close]') || this.dialog;
      window.requestAnimationFrame(
        function () {
          if (target) target.focus();
        }.bind(this)
      );
    }

    close() {
      if (!this.isOpen) return;
      this.setAttribute('hidden', '');
      document.body.classList.remove('size-guide-modal-open');
      document.removeEventListener('keydown', this.handleKeydown);

      if (this.trigger && document.body.contains(this.trigger)) {
        this.trigger.focus();
      }
      this.trigger = null;
    }

    handleOverlayClick(event) {
      if (event.target === this.overlay) this.close();
    }

    focusableItems() {
      if (!this.dialog) return [];
      return Array.prototype.slice.call(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (element) {
        return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
      });
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = this.focusableItems();
      if (items.length === 0) {
        event.preventDefault();
        if (this.dialog) this.dialog.focus();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      var insideDialog = this.dialog && this.dialog.contains(active);

      if (event.shiftKey) {
        if (active === first || active === this.dialog || !insideDialog) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !insideDialog) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('size-guide-modal')) {
    customElements.define('size-guide-modal', SizeGuideModal);
  }

  // Delegated so the trigger keeps working after the variant picker re-renders.
  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    var trigger = event.target.closest('[data-size-guide-open]');
    if (!trigger) return;

    var modalId = trigger.getAttribute('aria-controls');
    var modal = modalId ? document.getElementById(modalId) : null;
    if (!modal) modal = document.querySelector('size-guide-modal');
    if (!modal || typeof modal.open !== 'function') return;

    event.preventDefault();
    modal.open(trigger);
  });
})();
